import { LuClock3 } from "react-icons/lu";

import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  Tooltip,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import { ChangeEvent, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { FaRegFileExcel } from "react-icons/fa";
import { GiPill } from "react-icons/gi";
import { MdSos } from "react-icons/md";
import { useLocation, useNavigate } from "react-router";
import Swal from "sweetalert2";
import { IMAGES } from "../../assets/images";
import FloatingButtonContainer from "../../components/floatingButton";
import FullScreenLoader from "../../components/fullScreenLoader";
import Header from "../../components/header";
import ScrollToTopButton from "../../components/scrollToTopButton";
import { CONSTANTS } from "../../constants";
import PillModal from "../../modals/pillsModal";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import {
  fetchDoctors,
  fetchGuides,
  fetchPatientById,
  patientsQueryKey,
  useSaveHistoryMutation,
} from "../../network/globalFetches";
import { Doctor, Guide, HistoryData, Patient, Pill } from "../../types";
import {
  AddDoctorToPatientResponse,
  GetHistoryByPatientResponse,
  TodayHistoryResponse,
} from "../../types/responses";
import { createExcelFileByMonth, PARTS_OF_THE_DAY } from "../../utils";

const fetchTodayHistory = (id: string) => async () => {
  const { data } = await fetcher().get<TodayHistoryResponse>(
    API.GET.TODAY_HISTORY_BY_PATIENT_ID(id)
  );

  return data;
};

const todayQueryKey = (patientId: string) => ["todayHistory", patientId];

const updateDoctorForPatient = async ({
  patientId,
  doctorId,
}: {
  patientId: string;
  doctorId: string;
}) => {
  const { data } = await fetcher().patch<AddDoctorToPatientResponse>(
    API.PATCH.EDIT_DOCTOR(patientId),
    {
      doctorId,
    }
  );
  return data;
};

const DropdownSelect = ({
  doctors,
  patientId,
  handleSavedDoctor,
}: {
  doctors: Doctor[];
  patientId: string;
  handleSavedDoctor: (doctorId: Doctor["_id"] | undefined) => void;
}) => {
  const [selectedOption, setSelectedOption] = useState<
    Doctor["_id"] | undefined
  >();

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: updateDoctorForPatient,
    onSuccess: ({ updatedPatient }) => {
      queryClient.setQueryData(
        patientsQueryKey,
        (oldPatients: Patient[] | undefined) => {
          if (!oldPatients) return [];
          return oldPatients.map((patient) =>
            patient._id === updatedPatient._id ? updatedPatient : patient
          );
        }
      );

      handleSavedDoctor(updatedPatient.doctorId);
      toast.success("הרופא נשמר בהצלחה");
    },
    onError: () => {
      toast.error("הרופא לא נשמר לצערינו");
    },
  });

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(event.target.value);
  };

  return (
    <div className="w-full max-w-sm mx-auto mt-2">
      <label
        htmlFor="doctors"
        className="block text-sm font-medium text-gray-700">
        בחר רופא מטפל
      </label>
      <select
        id="doctors"
        value={selectedOption}
        onChange={handleSelectChange}
        className="mt-2 block w-full p-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
        <option value="">רופא מטפל</option>
        {doctors.map((option) => (
          <option key={option._id} value={option._id}>
            {option.name}
          </option>
        ))}
      </select>

      {selectedOption && (
        <button
          onClick={() => {
            mutate({ patientId, doctorId: selectedOption });
          }}
          className="border py-2 px-4 mt-2 rounded bg-[#144082] text-white">
          שמור רופא
        </button>
      )}
    </div>
  );
};

const getHistoryByPatientId = async (
  patientId: string,
  startDate?: string,
  endDate?: string
) => {
  const { data } = await fetcher().get<GetHistoryByPatientResponse>(
    API.GET.GET_HISTORY_BY_PATIENT(patientId, startDate, endDate)
  );

  return data;
};

function transformData<T extends "guides" | "time">(
  history: HistoryData,
  showBy: T
): T extends "guides"
  ? { title: string; data: Record<string, Record<string, Pill[]>> }[]
  : { title: string; data: Record<string, (Pill & { guide: string })[]> }[] {
  if (showBy === "guides") {
    // Transform for `showBy === "guides"`
    return Object.keys(history).map((date) => ({
      title: date,
      data: Object.keys(history[date]).reduce((acc, partOfTheDay) => {
        acc[partOfTheDay] = Object.keys(history[date][partOfTheDay]).reduce(
          (guideAcc, guideId) => {
            guideAcc[guideId] = history[date][partOfTheDay][guideId];
            return guideAcc;
          },
          {} as Record<string, Pill[]>
        );
        return acc;
      }, {} as Record<string, Record<string, Pill[]>>),
    })) as any; // Casting to any to satisfy conditional type inference
  } else {
    const partOfTheDayOrder = ["night", "evening", "noon", "morning"];
    return Object.keys(history).map((date) => {
      const reorderedParts = Object.keys(history[date]).sort(
        (a, b) => partOfTheDayOrder.indexOf(a) - partOfTheDayOrder.indexOf(b)
      );

      return {
        title: date,
        data: reorderedParts.reduce((acc, partOfTheDay) => {
          acc[partOfTheDay] = Object.entries(history[date][partOfTheDay])
            .flatMap(([guideId, pills]) =>
              pills.map((pill) => ({
                ...pill,
                guideId,
              }))
            )
            .sort((a, b) =>
              dayjs(a.time, "HH:mm").isAfter(dayjs(b.time, "HH:mm")) ? -1 : 1
            );

          return acc;
        }, {} as Record<string, Pill[]>),
      };
    }) as any; // Casting to any to satisfy conditional type inference
  }
}

const SectionListByTime = ({
  sectionData,
  showAllDates,
  sectionChecked,
  guides,
  setSectionChecked,
}: {
  sectionData: ReturnType<typeof transformData<"time">>;
  showAllDates: boolean;
  sectionChecked: number | undefined;
  guides: Guide[] | undefined;
  setSectionChecked: React.Dispatch<React.SetStateAction<number | undefined>>;
}) => {
  return sectionData.map((section, index) => (
    <div key={section.title} className="mb-8 bg-white rounded-lg shadow-lg p-5">
      {/* Section Title */}
      <div
        className="hover:cursor-pointer"
        onClick={() => {
          setSectionChecked(sectionChecked === index ? undefined : index);
        }}>
        <h2 className="text-lg sm:text-2xl font-bold text-gray-800 border-b-2 border-blue-500 pb-2 mb-4">
          {section.title}
          {" - "}
          {dayjs(section.title, "DD-MM-YY").format("dddd")}
        </h2>
      </div>
      <Collapse in={showAllDates || sectionChecked === index}>
        {Object.entries(section.data).map(([partOfTheDay, pills]) => (
          <div key={partOfTheDay} className="mb-4">
            {/* Part of the Day */}
            <h3 className="text-xl font-bold text-blue-800 mb-2">
              {/* @ts-ignore */}
              {PARTS_OF_THE_DAY[partOfTheDay].labelHe}
            </h3>

            {/* Pills */}
            <ul className="space-y-2">
              {pills.map((pill, i) => {
                const guide = guides?.find((g) => g._id === pill.guideId)?.name;
                return (
                  <li
                    key={pill.date + pill.time + i}
                    className={`flex justify-between gap-6 items-center p-3 ${
                      pill.isSos ? "bg-red-100" : "bg-gray-50"
                    } border border-gray-300 rounded-lg shadow-sm mb-2`}>
                    <div className="flex flex-2 gap-6 text-right">
                      <span className="flex justify-center items-center gap-1 text-sm">
                        <LuClock3 />
                        {pill.time}
                      </span>
                      <span className="flex-1 text-sm font-medium text-gray-700">
                        {pill.pillName}
                      </span>
                    </div>
                    <div className="flex flex-1 justify-end gap-4">
                      <span className="hidden sm:inline-block text-right text-sm">
                        {guide ? `מלווה: ${guide}` : ""}
                      </span>
                      <span className="italic text-gray-500 text-sm">
                        {pill.dosage} מ״ג
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </Collapse>
    </div>
  ));
};

const SectionListByGuide = ({
  sectionData,
  guides,
  showAllDates,
  sectionChecked,
  setSectionChecked,
}: {
  sectionData: ReturnType<typeof transformData<"guides">>;
  guides: Guide[] | undefined;
  showAllDates: boolean;
  sectionChecked: number | undefined;
  setSectionChecked: React.Dispatch<React.SetStateAction<number | undefined>>;
}) => {
  return sectionData.map((section, index) => (
    <div key={section.title} className="mb-8 bg-white rounded-lg shadow-lg p-5">
      {/* Section Title */}
      <div
        className="hover:cursor-pointer"
        onClick={() => {
          setSectionChecked(sectionChecked === index ? undefined : index);
        }}>
        <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-blue-500 pb-2 mb-4">
          {section.title}
          {" - "}
          {dayjs(section.title, "DD-MM-YY").format("dddd")}
        </h2>
      </div>
      <Collapse in={showAllDates || sectionChecked === index}>
        {Object.entries(section.data).map(([partOfTheDay, history]) => (
          <div key={partOfTheDay} className="mb-4">
            {/* Part of the Day */}
            <h3 className="text-xl font-bold text-blue-800 mb-2">
              {/* @ts-ignore */}
              {PARTS_OF_THE_DAY[partOfTheDay].labelHe}
            </h3>

            {/* Pills */}
            <ul className="space-y-2">
              {Object.entries(history).map(([guideId, pills]) => (
                <div key={guideId}>
                  <div className="mb-2 mt-4">
                    <span className="text-blue-950 font-semibold">
                      {" מלווה מאשר: "}
                      {guides?.find((g) => g._id === guideId)?.name || ""}
                    </span>
                  </div>
                  {pills.map((pill, i) => {
                    return (
                      <li
                        key={pill.date + pill.time + i}
                        className={`flex justify-between gap-4 items-center p-3 ${
                          pill.isSos ? "bg-red-100" : "bg-gray-50"
                        } border border-gray-300 rounded-lg shadow-sm mb-2`}>
                        <div className="flex flex-2 gap-6 text-right">
                          <span className="flex justify-center items-center gap-1">
                            <LuClock3 />
                            {pill.time}
                          </span>
                          <span className="font-medium text-gray-700">
                            {pill.pillName}
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <span className="italic text-gray-500 w-10">
                            {pill.dosage} מ״ג
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </div>
              ))}
            </ul>
          </div>
        ))}
      </Collapse>
    </div>
  ));
};

const SectionList = ({
  data,
  patient,
}: {
  data: GetHistoryByPatientResponse;
  patient: Patient;
}) => {
  const initialShowSectionBy: "guides" | "time" = "time";
  const { data: guides } = useQuery({
    queryKey: fetchGuides.queryKey,
    queryFn: fetchGuides.fetch,
    staleTime: Infinity,
  });
  const [showAllDates, setShowAllDates] = useState<boolean>(false);
  const [sectionChecked, setSectionChecked] = useState<number | undefined>(0);
  const [showSectionBy, setShowSectionBy] = useState<"guides" | "time">(
    initialShowSectionBy
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowSectionBy(
      (event.target as HTMLInputElement).value as "guides" | "time"
    );
  };

  const renderSection = (_: Patient) => {
    if (showSectionBy === "guides") {
      const sectionData = useMemo(
        () => transformData(data.history, showSectionBy),
        [data.history, showSectionBy]
      );
      return (
        <SectionListByGuide
          showAllDates={showAllDates}
          sectionData={sectionData}
          guides={guides}
          sectionChecked={sectionChecked}
          setSectionChecked={setSectionChecked}
        />
      );
    } else {
      const sectionData = useMemo(
        () => transformData(data.history, showSectionBy),
        [data.history, showSectionBy]
      );
      return (
        <SectionListByTime
          showAllDates={showAllDates}
          sectionData={sectionData}
          sectionChecked={sectionChecked}
          guides={guides}
          setSectionChecked={setSectionChecked}
        />
      );
    }
  };

  return (
    <div className="p-3 sm:p-5 bg-gray-100 w-11/12 sm:w-2/3 mx-auto rounded">
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <label
            className="hover:cursor-pointer"
            onClick={() => setShowAllDates((prev) => !prev)}>
            פתח הכל
          </label>
          <Switch
            checked={showAllDates}
            onChange={() => setShowAllDates((prev) => !prev)}
          />
        </div>
        <h1 className="flex-1 text-center text-xl sm:text-2xl mb-2 font-black">
          היסטוריה
        </h1>
        <div className="flex-1">
          <FormControl>
            <FormLabel
              className="text-center"
              id="controlled-radio-buttons-group">
              בחר סוג מיון
            </FormLabel>
            <RadioGroup
              aria-labelledby="controlled-radio-buttons-group"
              row
              defaultValue={initialShowSectionBy}
              name="radio-buttons-group"
              onChange={handleChange}>
              <FormControlLabel
                value="guides"
                control={<Radio size="small" />}
                label="מלווים"
              />
              <FormControlLabel
                value="time"
                control={<Radio size="small" />}
                label="זמן"
              />
            </RadioGroup>
          </FormControl>
        </div>
      </div>
      {renderSection(patient)}
    </div>
  );
};

const openPopup = async (
  guides: Guide[] | undefined,
  patient: Patient,
  history: TodayHistoryResponse["history"]
) => {
  // Filter SoS pills
  const sosPills = patient.pills.filter((pill) => pill.isSOS);

  // Generate SOS pills dropdown options
  const pillOptions = sosPills
    .map((pill) => `<option value="${pill._id}">${pill.dragHebName}</option>`)
    .join("");

  // Generate guide options
  const guideOptions = guides
    ?.map((guide) => `<option value="${guide._id}">${guide.name}</option>`)
    .join("");

  return Swal.fire({
    title: "לקיחת כדור SoS",
    html: `
      <div style="display: flex; flex-direction: column;">
        <select id="swal-pill-select" class="swal2-input" style="border: 1px solid black; border-radius: 8px;">
          <option value="">בחר כדור</option>
          ${pillOptions}
          <option value="other">אחר...</option>
        </select>
        <p id="notes" style="margin-bottom: 1em;"></p>
        <p id="sos-pill-info" style="font-weight: bold;"></p>
        <input id="swal-pill-input" class="swal2-input" placeholder="הזן שם כדור" type="text" style="display: none; border: 1px solid black; border-radius: 8px;">
        <input id="swal-input2" class="swal2-input" placeholder="מינון במ״ג - (לא חובה)" style="margin-bottom: 20px; border: 1px solid black; border-radius: 8px;">
        <select id="swal-input3" class="swal2-input" style="border: 1px solid black; border-radius: 8px;">
          <option value="">בחר מלווה מאשר</option>
          ${guideOptions}
        </select>
        <div style="display: flex;">
        </div>
        <label style="display:none;" id="managerConfirmedLabel">
          <input id="managerConfirmed" type="checkbox" />
          יש אישור מנהל?
        </label>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: "אישור",
    cancelButtonText: "ביטול",
    confirmButtonColor: "#c74292",
    didOpen: () => {
      // Show input if 'Other' is selected
      const pillSelect = document.getElementById(
        "swal-pill-select"
      ) as HTMLSelectElement;
      const pillInput = document.getElementById(
        "swal-pill-input"
      ) as HTMLInputElement;
      const dosageInput = document.getElementById(
        "swal-input2"
      ) as HTMLInputElement;
      const pillInfo = document.getElementById(
        "sos-pill-info"
      ) as HTMLParagraphElement;
      const notes = document.getElementById("notes") as HTMLParagraphElement;

      pillSelect.addEventListener("change", () => {
        if (pillSelect.value === "other") {
          pillInput.style.display = "block";
          pillInfo.textContent = "";
          notes.textContent = "";
        } else {
          pillInput.style.display = "none";
          pillInput.value = "";

          if (!history) {
            return;
          }

          const sos = sosPills.find((sos) => {
            return sos._id === pillSelect.value;
          });
          dosageInput.value =
            //@ts-ignore
            sos?.dosages?.length > 0
              ? //@ts-ignore
                `${Object.values(sos?.dosages?.[0])[0]?.value}` || ""
              : "";

          notes.textContent = `הערות רופא: ${sos?.notes || "אין הערות"}` || "";

          const times = history
            ?.filter((h) => h.isSOS)
            .filter((sosHistory) => sosHistory.title === sos?.dragHebName);

          pillInfo.textContent = sos
            ? `הכדור נלקח ${times?.length || "0"} מתוך ${
                sos.numberOfSosTime
              } פעמים`
            : "";
        }
      });
    },
    preConfirm: () => {
      const pillSelect = document.getElementById(
        "swal-pill-select"
      ) as HTMLSelectElement;
      const pillInput = document.getElementById(
        "swal-pill-input"
      ) as HTMLInputElement;
      const pillInfo = document.getElementById(
        "sos-pill-info"
      ) as HTMLParagraphElement;
      const managerLabel = document.getElementById(
        "managerConfirmedLabel"
      ) as HTMLLabelElement;
      const managerCheckbox = document.getElementById(
        "managerConfirmed"
      ) as HTMLInputElement;
      const dosageValue = (
        document.getElementById("swal-input2") as HTMLInputElement
      ).value;
      const selectValue = (
        document.getElementById("swal-input3") as HTMLSelectElement
      ).value;
      const managerCheckboxValue = managerCheckbox.checked;

      const sos = sosPills.find((sos) => {
        return sos._id === pillSelect.value;
      });

      const selectedPill =
        pillSelect.value === "other" ? pillInput.value : sos?.dragHebName || "";

      const times = history
        ?.filter((h) => h.isSOS)
        .filter((sosHistory) => sosHistory.title === sos?.dragHebName);

      if (
        times &&
        sos &&
        times.length >= sos.numberOfSosTime &&
        !managerCheckboxValue
      ) {
        managerLabel.style.display = "block";
        alert("צריך אישור מנהל כדי לתת יותר כדורים מהמקסימום");
        return false;
      }

      pillInfo.textContent = sos
        ? `הכדור נלקח ${times?.length || "0"} מתוך ${
            sos.numberOfSosTime
          } פעמים היום`
        : "";

      if (!selectedPill || !selectValue) {
        Swal.showValidationMessage("חובה למלא את כל השדות");
        return false;
      }

      return {
        textValue: selectedPill,
        selectValue,
        dosageValue: dosageValue ? dosageValue : "0",
      };
    },
  });
};

const PatientDetailsPage = () => {
  const navigate = useNavigate();
  const patientId = useLocation().state.patientId as string;
  const [showDoctorDropdown, setShowDoctorDropdown] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: guides } = useQuery({
    queryKey: fetchGuides.queryKey,
    queryFn: fetchGuides.fetch,
    staleTime: Infinity,
  });
  const { data: doctors } = useQuery({
    queryKey: fetchDoctors.queryKey,
    queryFn: fetchDoctors.fetch,
    staleTime: Infinity,
  });
  const { data: patient, isLoading: isLoadingPatient } = useQuery({
    queryKey: fetchPatientById.queryKey(patientId),
    queryFn: fetchPatientById.fetch(patientId),
  });

  const { data: todayHistory } = useQuery({
    queryKey: todayQueryKey(patient?._id || ""),
    queryFn: fetchTodayHistory(patient?._id || ""),
    enabled: patient?._id !== undefined,
  });

  const {
    data: history,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ["history", patient?._id || ""],
    queryFn: () => getHistoryByPatientId(patient?._id || ""),
    enabled: patient?._id !== undefined,
  });

  const { mutate } = useSaveHistoryMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: todayQueryKey(patient?._id || ""),
      });
      toast.success("נשמר להסטוריה בהצלחה");
    },
    onError: () => {
      toast.error("שגיאה - לא הצלחנו לשמור להסטוריה");
    },
  });

  if (isLoadingPatient) return <FullScreenLoader />;
  if (isLoading) return <FullScreenLoader />;
  if (!patient) return <p>No patient found</p>;
  if (!history) return <p>No history found</p>;

  if (isError) return <p>Error loading history</p>;

  const doctorName =
    doctors && doctors.find((doctor) => doctor._id === patient.doctorId)?.name;

  const hasSOS = patient.pills.some((p) => p.isSOS);
  return (
    <div className="container mx-auto h-screen flex flex-col">
      <Header>
        <Header.Time />
        <Header.Title title={`הפרופיל של ${patient.name}`} />
        <Header.Logo />
      </Header>

      {doctorName ? (
        <div className="text-center font-bold text-xl">
          <h2>
            <span className="font-normal">רופא מטפל:</span> {doctorName}
          </h2>
        </div>
      ) : (
        showDoctorDropdown &&
        doctors && (
          <div className="text-center">
            <p>הוסף רופא מטפל מהרשימה:</p>
            <DropdownSelect
              patientId={patient._id}
              doctors={doctors}
              handleSavedDoctor={(doctorId) => {
                patient.doctorId = doctorId;
                setShowDoctorDropdown(false);
              }}
            />
          </div>
        )
      )}

      {patient.enterDateToHome && patient.exitDateFromHome && (
        <>
          <div className="text-center font-bold text-xl">
            <p className="text-base">
              <span className="font-normal">תאריך כניסה</span>{" "}
              {dayjs(patient.enterDateToHome).format("DD/MM/YY")}
              {" - "}
              <span className="font-normal">תאריך יציאה</span>{" "}
              {dayjs(patient.exitDateFromHome).format("DD/MM/YY")}
            </p>
          </div>
          <div>
            <p className="text-center text-base">
              נשארו{" "}
              <span className="font-semibold">
                {dayjs(patient.exitDateFromHome).diff(dayjs(), "day")}
              </span>{" "}
              {" ימים לסיום בבית"}
            </p>
          </div>
        </>
      )}

      {!isEmpty(patient.pills) ? (
        <button
          onClick={() => {
            setIsModalOpen(true);
          }}
          className="bg-[#144082] text-white rounded py-2 px-4 sm:py-5 sm:px-10 sm:w-1/3 mx-auto shadow-xl mt-6">
          כדורים לבליעה
        </button>
      ) : (
        <button
          onClick={() => {
            navigate(CONSTANTS.ROUTES.pillsManager, {
              state: { patientId: patient._id },
            });
          }}
          className="bg-[#144082] text-white rounded py-5 px-10 w-1/3 mx-auto shadow-xl mt-6">
          להוספת כדורים
        </button>
      )}

      <section className="py-6">
        {!isEmpty(history.history) ? (
          <SectionList data={history} patient={patient} />
        ) : (
          <div className="flex flex-col text-center gap-2">
            <h2 className="font-bold text-2xl">אין הסטוריית כדורים שנלקחו</h2>
            <div className="w-1/3 mx-auto">
              <img src={IMAGES.NO_HISTORY} className="object-contain" />
            </div>
          </div>
        )}
      </section>

      {isModalOpen && (
        <PillModal
          patient={patient}
          doctors={doctors}
          onClose={() => setIsModalOpen(false)}
          onSave={({ guideId, patientId, data }) => {
            mutate({
              guideId,
              patientId,
              date: dayjs().toISOString(),
              pills: data,
            });
          }}
        />
      )}
      <ScrollToTopButton />
      <FloatingButtonContainer>
        <Tooltip
          title="יצא קובץ אקסל"
          placement="right"
          className="hover:scale-105">
          <FloatingButtonContainer.Button
            icon={<FaRegFileExcel className="text-2xl" />}
            onClick={async () => {
              if (isEmpty(history.history)) {
                toast.error("לדייר אין הסטוריית כדורים");
                return;
              }
              createExcelFileByMonth(
                history.history,
                guides || [],
                patient.name
              );
            }}
          />
        </Tooltip>
        {hasSOS && (
          <Tooltip
            title="כדור S.O.S"
            placement="right"
            className="hover:scale-105">
            <FloatingButtonContainer.Button
              icon={<MdSos className="text-2xl" />}
              onClick={async () => {
                const { isConfirmed, value } = await openPopup(
                  guides,
                  patient,
                  todayHistory?.history
                );
                if (isConfirmed) {
                  const { textValue, selectValue, dosageValue } = value;

                  mutate({
                    guideId: selectValue,
                    patientId: patient._id,
                    date: dayjs().toISOString(),
                    pills: [
                      {
                        isSos: true,
                        pillId: "111111111111111111111111",
                        pillName: textValue,
                        dosages: [{ value: dosageValue, partOfTheDay: "sos" }],
                      },
                    ],
                  });
                }
              }}
            />
          </Tooltip>
        )}
        <Tooltip
          title="מנהל התרופות"
          placement="right"
          className="hover:scale-105">
          <FloatingButtonContainer.Button
            icon={<GiPill className="text-2xl" />}
            onClick={() => {
              navigate(CONSTANTS.ROUTES.pillsManager, {
                state: { patientId: patient._id },
              });
            }}
          />
        </Tooltip>
      </FloatingButtonContainer>
      <Toaster position="bottom-center" />
    </div>
  );
};

export default PatientDetailsPage;
